import { useEffect, useState } from "react";
import { getSkillInTheWild } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * SkillInTheWildImage — sibling visual for the SITW card. Renders the
 * daily Nano-Banana-generated editorial illustration that matches
 * today's career story. Hidden for free-tier / signed-out teachers
 * (they see the simplified text-only card on the homepage).
 *
 * Shares the same `/api/skill-in-the-wild` payload as the text card,
 * which is cached server-side per teacher per day, so this is a single
 * extra HTTP request, not an extra LLM call.
 */
export const SkillInTheWildImage = ({ refreshKey = 0, className = "" }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // BETA OVERRIDE: same as in SkillInTheWildCard — treat everyone as
  // paid during beta so the illustration is visible. Flip back to
  // false when Stripe is live and the gate should apply.
  const BETA_TREAT_AS_PAID = true;
  const isPaid = BETA_TREAT_AS_PAID || Boolean(
    user?.subscription?.status &&
      ["active", "trialing"].includes(user.subscription.status)
  );

  useEffect(() => {
    if (!isPaid) {
      setLoading(false);
      setImageUrl(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    getSkillInTheWild()
      .then((d) => {
        if (!alive) return;
        setImageUrl(d?.image_url || null);
      })
      .catch(() => {
        if (alive) setImageUrl(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, isPaid]);

  // Free/signed-out: render nothing (the homepage falls back to a
  // single-column SITW card layout).
  if (!isPaid) return null;

  return (
    <aside
      data-testid="skill-in-the-wild-image"
      className={`ae-card overflow-hidden p-0 shadow-md ${className}`}
    >
      <div
        className="aspect-square w-full bg-[#f4f5f7] grid place-items-center overflow-hidden"
        data-testid="skill-in-the-wild-image-frame"
      >
        {loading ? (
          <div
            data-testid="skill-in-the-wild-image-loading"
            className="h-full w-full bg-gradient-to-br from-[#f4f5f7] to-[#e8edf2] animate-pulse"
          />
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="An editorial illustration matching today's skill story"
            data-testid="skill-in-the-wild-image-img"
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span
            data-testid="skill-in-the-wild-image-fallback"
            className="text-xs text-[#4a6984]/50 font-medium px-6 text-center"
          >
            Today&apos;s illustration is being generated.
          </span>
        )}
      </div>
    </aside>
  );
};

export default SkillInTheWildImage;
